import { ComponentRef } from '@wix/editor-platform-sdk-types';
import { TPAMessageDataAny, CallHandler, SdkEvent } from '../types/tpaMessage';

const Intents = {
  TPA_MESSAGE: 'TPA',
  TPA_MESSAGE2: 'TPA2',
  TPA_RESPONSE: 'TPA_RESPONSE',
  TPA_PREVIEW: 'TPA_PREVIEW',
  UI_LIB_RESPONSE: 'UI_LIB_RESPONSE',
  PINGPONG_PREFIX: 'pingpong:',
};

const allowedTPAIntents = [
  Intents.TPA_MESSAGE,
  Intents.TPA_MESSAGE2,
  Intents.TPA_PREVIEW,
];

const callPostMessage = (
  source: SdkEvent['source'],
  msgData: TPAMessageDataAny,
  targetOrigin: string = '*',
) => {
  if (!source) {
    return;
  }

  let msgStr = '';

  try {
    msgStr = JSON.stringify(msgData);
  } catch (e) {
    return;
  }

  if (!source.postMessage) {
    // Narrowing for possible iframe or WindowProxy
    if (
      typeof (source as any).contentWindow !== 'undefined' &&
      (source as any).contentWindow !== null
    ) {
      source = (source as any).contentWindow;
    }
  }

  if (source && typeof source.postMessage === 'function') {
    source.postMessage(msgStr, { targetOrigin });
  }
};

const generateResponseFunction = (
  source: SdkEvent['source'],
  msgData: TPAMessageDataAny,
) => {
  const uiLibHandlers = [
    'openColorPicker',
    'openFontPicker',
    'openBackgroundPicker',
  ];
  let intent = Intents.TPA_RESPONSE;

  if (uiLibHandlers.includes(msgData.type)) {
    intent = Intents.UI_LIB_RESPONSE;
  }

  return (data: TPAMessageDataAny) => {
    try {
      callPostMessage(source, {
        intent,
        callId: msgData.callId,
        type: msgData.type,
        res: data,
        status: true,
      });
    } catch (error) {
      throw Error(
        `SdkPostMessageCommon -> error executing callPostMessage,  ${error}`,
      );
    }
  };
};

const isTPAMessage = (msgData: TPAMessageDataAny) => {
  return (
    allowedTPAIntents.includes(msgData.intent) && !originatedFromNonTPA(msgData)
  );
};

const originatedFromNonTPA = (msgData: TPAMessageDataAny) => {
  return msgData.compId === '[UNKNOWN]';
};

const handleTPAMessage = (callHandler: CallHandler, event: SdkEvent) => {
  let msg;
  try {
    if (event.data) {
      if (typeof event.data === 'string') {
        msg = JSON.parse(event.data);
      }
    } else if (event.originalEvent?.data) {
      event = event.originalEvent;
      if (typeof event.data === 'string') {
        msg = JSON.parse(event.data);
      }
    }
  } catch (e) {
    return;
  }

  if (msg && isTPAMessage(msg)) {
    callHandler(msg, generateResponseFunction(event.source, msg));
  }
};

const getOrigCompIdFromTpaMessage = (
  msg: TPAMessageDataAny,
  selectedComponent: ComponentRef,
) => {
  return msg.originalCompId || selectedComponent.id;
};

export {
  Intents,
  callPostMessage,
  generateResponseFunction,
  isTPAMessage,
  handleTPAMessage,
  getOrigCompIdFromTpaMessage,
};
